import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  History,
  Coins,
  Gift,
  RefreshCw,
  CheckCircle,
  X,
  Plus,
  ChevronRight,
  Trash2,
  Sparkles,
  ShoppingBag,
  Users,
  Zap,
  AlertCircle
} from 'lucide-react';
import { Elements, CardElement, PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { useTestMode } from '../contexts/TestModeContext';
import { useGTTransactions, useTokenPackages } from '../hooks/useFirebaseData';
import { getStripe, formatPrice, pricePerToken } from '../config/stripe';
import { TokenPackage } from '../types';
import { dbSet, dbGet, dbRemove, callFunction } from '../config/firebase';
import { TestModeWarning } from '../components/common/TestModeBanner';
import { GTChange } from '../components/common/GTDisplay';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { SignupOfferWelcomeModal } from '../components/common/SignupOfferWelcomeModal';
import { GTTransaction } from '../types';
import { format } from 'date-fns';

// Saved card type
interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
  createdAt: number;
  stripeMode: 'test' | 'live'; // Which Stripe environment this card belongs to
  verified?: boolean; // Whether this card was verified via SetupIntent
}

// Cloud Function response types
interface PaymentIntentRequest {
  packageId: string;
  paymentMethodId: string;
  dev?: boolean;
}

interface PaymentIntentResponse {
  success: boolean;
  tokens?: number;
  paymentIntentId?: string;
  requiresAction?: boolean;
  clientSecret?: string;
  error?: string;
}

// Stripe Card styling
const cardStyle = {
  style: {
    base: {
      color: '#0F172A',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#64748B'
      }
    },
    invalid: {
      color: '#EF4444',
      iconColor: '#EF4444'
    }
  }
};

// Get card brand icon/color
const getCardBrandStyles = (brand: string) => {
  switch (brand.toLowerCase()) {
    case 'visa':
      return { color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/30' };
    case 'mastercard':
      return { color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/30' };
    case 'amex':
      return { color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/30' };
    default:
      return { color: 'text-gray-600', bg: 'bg-gray-50 dark:bg-gray-800' };
  }
};

// GT Intro Box Component
const GTIntroBox: React.FC<{ onDismiss: () => void }> = ({ onDismiss }) => {
  const features = [
    { icon: ShoppingBag, title: 'GT Store', desc: 'Redeem for merch & rewards' },
    { icon: Users, title: 'Tip Creators', desc: 'Support your favorite bettors' },
    { icon: Zap, title: 'SweatRooms', desc: 'Join exclusive live rooms' },
    { icon: Sparkles, title: 'Boost Posts', desc: 'Get more visibility' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20, height: 0, marginBottom: 0 }}
      className="relative overflow-hidden rounded-2xl mb-8"
    >
      {/* Background with gradient overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1639762681485-074b7f938ba0?q=80&w=2832&auto=format&fit=crop')`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/80 to-black/70" />
      <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-transparent to-primary/20" />

      {/* Animated particles/glow effect */}
      <div className="absolute top-10 right-20 w-32 h-32 bg-primary/30 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-10 left-20 w-24 h-24 bg-primary/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />

      {/* Content */}
      <div className="relative z-10 p-6 sm:p-8">
        {/* Close button */}
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Dismiss"
        >
          <X size={18} className="text-white" />
        </button>

        <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
          {/* Left side - Main content */}
          <div className="flex-1 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/20 backdrop-blur-sm rounded-full border border-primary/30">
              <Coins size={16} className="text-primary" />
              <span className="text-sm font-medium text-primary">GroundTokens</span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
              Your Gateway to the<br />
              <span className="text-primary">ParlayGround Economy</span>
            </h2>

            <p className="text-white/70 max-w-md text-sm sm:text-base">
              Earn GT through engagement, purchase packs, and unlock exclusive features across the platform. The more you participate, the more you earn!
            </p>
          </div>

          {/* Right side - Feature grid */}
          <div className="grid grid-cols-2 gap-3 lg:w-auto">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 + i * 0.1 }}
                className="flex items-center gap-3 p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 hover:bg-white/15 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <feature.icon size={20} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-white text-sm truncate">{feature.title}</p>
                  <p className="text-white/60 text-xs truncate">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Purchase Flow Modal
type PurchaseStep = 'select-package' | 'select-payment' | 'processing';

const PurchaseFlowModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (amount: number) => void;
  userId: string;
  savedCards: SavedCard[];
  onCardSaved: () => void;
  packages: TokenPackage[];
  isTestMode: boolean;
}> = ({ isOpen, onClose, onSuccess, userId, savedCards, onCardSaved, packages, isTestMode }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [step, setStep] = useState<PurchaseStep>('select-package');
  const [selectedPackage, setSelectedPackage] = useState<TokenPackage | null>(null);
  const [selectedCard, setSelectedCard] = useState<SavedCard | null>(null);
  const [useNewCard, setUseNewCard] = useState(true);
  const [saveCard, setSaveCard] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const [canMakePayment, setCanMakePayment] = useState(false);

  // Setup Payment Request (Apple Pay / Google Pay)
  useEffect(() => {
    if (!stripe || !selectedPackage) {
      setPaymentRequest(null);
      setCanMakePayment(false);
      return;
    }

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: `${selectedPackage.tokens + selectedPackage.bonusTokens} GT`,
        amount: selectedPackage.priceUsdCents,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(pr);
        setCanMakePayment(true);
      } else {
        setCanMakePayment(false);
      }
    });

    // Handle payment method from Apple Pay / Google Pay
    pr.on('paymentmethod', async (ev) => {
      setLoading(true);
      setError(null);
      setStep('processing');

      try {
        const createPaymentIntent = callFunction<PaymentIntentRequest, PaymentIntentResponse>('createPaymentIntent');
        const result = await createPaymentIntent({
          packageId: selectedPackage.packageId,
          paymentMethodId: ev.paymentMethod.id,
          dev: isTestMode
        });

        const response = result.data;

        if (response.success && response.tokens) {
          ev.complete('success');
          onSuccess(response.tokens);
        } else if (response.requiresAction && response.clientSecret) {
          const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
            response.clientSecret,
            { payment_method: ev.paymentMethod.id },
            { handleActions: false }
          );

          if (confirmError) {
            ev.complete('fail');
            throw new Error(confirmError.message || 'Authentication failed');
          }

          if (paymentIntent?.status === 'requires_action') {
            ev.complete('success');
            const { error: actionError } = await stripe.confirmCardPayment(response.clientSecret);
            if (actionError) {
              throw new Error(actionError.message || 'Authentication failed');
            }
            onSuccess(selectedPackage.tokens + selectedPackage.bonusTokens);
          } else if (paymentIntent?.status === 'succeeded') {
            ev.complete('success');
            onSuccess(selectedPackage.tokens + selectedPackage.bonusTokens);
          } else {
            ev.complete('fail');
            throw new Error('Payment was not completed');
          }
        } else {
          ev.complete('fail');
          throw new Error(response.error || 'Payment failed');
        }
      } catch (err: any) {
        const errorMessage = err.message || 'Purchase failed. Please try again.';
        setError(errorMessage);
        setStep('select-payment');
      } finally {
        setLoading(false);
      }
    });

    return () => {
      pr.off('paymentmethod');
    };
  }, [stripe, selectedPackage, onSuccess, isTestMode]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('select-package');
      setSelectedPackage(null);
      setError(null);
      setPaymentRequest(null);
      setCanMakePayment(false);
      // If user has saved cards, select the default one
      if (savedCards.length > 0) {
        const defaultCard = savedCards.find(c => c.isDefault) || savedCards[0];
        setSelectedCard(defaultCard);
        setUseNewCard(false);
      } else {
        setSelectedCard(null);
        setUseNewCard(true);
      }
    }
  }, [isOpen, savedCards]);

  const handlePackageSelect = (pkg: TokenPackage) => {
    setSelectedPackage(pkg);
    setStep('select-payment');
  };

  const handleBack = () => {
    if (step === 'select-payment') {
      setStep('select-package');
      setError(null);
    }
  };

  const handleDeleteCard = async (cardId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const basePath = isTestMode ? 'test-mode/v1' : 'v1';
      await dbRemove(`${basePath}/wallet/savedCards/${userId}/${cardId}`);
      onCardSaved();
      if (selectedCard?.id === cardId) {
        setSelectedCard(null);
        setUseNewCard(true);
      }
    } catch (err) {
      console.error('Failed to delete card:', err);
    }
  };

  const handlePurchase = async () => {
    if (!stripe || !selectedPackage) return;

    setLoading(true);
    setError(null);
    setStep('processing');

    try {
      let paymentMethodId: string;

      if (useNewCard) {
        if (!elements) throw new Error('Card element not initialized');
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) throw new Error('Card element not found');

        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement
        });

        if (pmError) throw new Error(pmError.message);
        if (!paymentMethod) throw new Error('Failed to create payment method');

        paymentMethodId = paymentMethod.id;

        // Save card if requested - use Stripe's paymentMethod.id as the card ID
        if (saveCard && paymentMethod.card) {
          const cardBrand = paymentMethod.card.brand || 'unknown';
          const cardLast4 = paymentMethod.card.last4 || '****';
          const cardExpMonth = paymentMethod.card.exp_month || 0;
          const cardExpYear = paymentMethod.card.exp_year || 0;
          const currentMode = isTestMode ? 'test' : 'live';

          // Check for duplicate card (same last4, brand, and expiry in current mode)
          const isDuplicate = savedCards.some(
            card => card.last4 === cardLast4 &&
                    card.brand === cardBrand &&
                    card.expMonth === cardExpMonth &&
                    card.expYear === cardExpYear
          );

          if (isDuplicate) {
            throw new Error(`This card (${cardBrand} ending in ${cardLast4}) is already saved.`);
          }

          const newCard: SavedCard = {
            id: paymentMethod.id,
            brand: cardBrand,
            last4: cardLast4,
            expMonth: cardExpMonth,
            expYear: cardExpYear,
            isDefault: savedCards.length === 0,
            createdAt: Date.now(),
            stripeMode: currentMode
          };
          const basePath = isTestMode ? 'test-mode/v1' : 'v1';
          await dbSet(`${basePath}/wallet/savedCards/${userId}/${paymentMethod.id}`, newCard);
          onCardSaved();
        }
      } else if (selectedCard) {
        paymentMethodId = selectedCard.id;
      } else {
        throw new Error('Please select a payment method');
      }

      // Call the Cloud Function to process the payment
      const createPaymentIntent = callFunction<PaymentIntentRequest, PaymentIntentResponse>('createPaymentIntent');
      const result = await createPaymentIntent({
        packageId: selectedPackage.packageId,
        paymentMethodId,
        dev: isTestMode
      });

      const response = result.data;

      if (response.success && response.tokens) {
        // Payment succeeded
        onSuccess(response.tokens);
      } else if (response.requiresAction && response.clientSecret) {
        // 3D Secure or additional authentication required
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          response.clientSecret
        );

        if (confirmError) {
          throw new Error(confirmError.message || 'Authentication failed');
        }

        if (paymentIntent?.status === 'succeeded') {
          // Payment confirmed after 3DS, tokens were already added by webhook/function
          // Refresh wallet data
          onSuccess(selectedPackage.tokens + selectedPackage.bonusTokens);
        } else {
          throw new Error('Payment was not completed');
        }
      } else {
        throw new Error(response.error || 'Payment failed');
      }
    } catch (err: any) {
      // Handle Firebase function errors
      const errorMessage = err.message || err.details || 'Purchase failed. Please try again.';
      setError(errorMessage);
      setStep('select-payment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-surface-dark rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border-light dark:border-border-dark">
          <div className="flex items-center gap-3">
            {step === 'select-payment' && (
              <button
                onClick={handleBack}
                className="p-1.5 hover:bg-surface-light dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ChevronRight size={20} className="rotate-180" />
              </button>
            )}
            <h2 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">
              {step === 'select-package' && 'Buy GroundTokens'}
              {step === 'select-payment' && 'Payment'}
              {step === 'processing' && 'Processing...'}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={step === 'processing'}
            className="p-2 hover:bg-surface-light dark:hover:bg-gray-800 rounded-full transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto max-h-[calc(90vh-140px)]">
          <AnimatePresence mode="wait">
            {/* Step 1: Select Package */}
            {step === 'select-package' && (
              <motion.div
                key="select-package"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-3"
              >
                {/* Test Mode Warning */}
                <TestModeWarning action="This purchase" />

                {packages.map((pkg) => (
                  <button
                    key={pkg.packageId}
                    onClick={() => handlePackageSelect(pkg)}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left hover:border-primary hover:bg-primary/5 ${
                      pkg.badge
                        ? 'border-primary bg-primary/5'
                        : 'border-border-light dark:border-border-dark'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <Coins size={24} className="text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-text-primary-light dark:text-text-primary-dark">
                              {pkg.name}
                            </h3>
                            {pkg.badge && (
                              <span className="px-2 py-0.5 bg-primary text-white text-xs font-medium rounded-full">
                                {pkg.badge}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                            {pkg.tokens.toLocaleString()} GT
                            {pkg.bonusTokens > 0 && (
                              <span className="text-success font-medium"> +{pkg.bonusTokens.toLocaleString()} bonus</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <p className="text-lg font-bold text-text-primary-light dark:text-text-primary-dark">
                          {formatPrice(pkg.priceUsdCents)}
                        </p>
                        <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                          {pricePerToken(pkg.priceUsdCents, pkg.tokens + pkg.bonusTokens)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </motion.div>
            )}

            {/* Step 2: Select Payment */}
            {step === 'select-payment' && selectedPackage && (
              <motion.div
                key="select-payment"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                {/* Order Summary */}
                <div className="bg-surface-light dark:bg-gray-800 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-text-primary-light dark:text-text-primary-dark">
                        {selectedPackage.name}
                      </p>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                        {selectedPackage.tokens.toLocaleString()} GT
                        {selectedPackage.bonusTokens > 0 && (
                          <span className="text-success"> +{selectedPackage.bonusTokens.toLocaleString()} bonus</span>
                        )}
                      </p>
                    </div>
                    <p className="text-xl font-bold text-primary">
                      {formatPrice(selectedPackage.priceUsdCents)}
                    </p>
                  </div>
                </div>

                {/* Apple Pay / Google Pay */}
                {canMakePayment && paymentRequest && (
                  <div className="mb-4">
                    <PaymentRequestButtonElement
                      options={{
                        paymentRequest,
                        style: {
                          paymentRequestButton: {
                            type: 'default',
                            theme: 'dark',
                            height: '48px',
                          },
                        },
                      }}
                    />
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border-light dark:border-border-dark" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-3 bg-white dark:bg-surface-dark text-text-secondary-light dark:text-text-secondary-dark">
                          Or pay with card
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Methods Section */}
                <div className="space-y-3">
                  <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark">
                    Payment Method
                  </h3>

                  {/* Saved Cards */}
                  {savedCards.map((card) => {
                    const styles = getCardBrandStyles(card.brand);
                    const isSelected = selectedCard?.id === card.id && !useNewCard;
                    return (
                      <div
                        key={card.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border-light dark:border-border-dark hover:border-primary/50'
                        }`}
                        onClick={() => {
                          setSelectedCard(card);
                          setUseNewCard(false);
                        }}
                      >
                        <div className={`w-10 h-10 ${styles.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <CreditCard size={20} className={styles.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-text-primary-light dark:text-text-primary-dark capitalize">
                            {card.brand} •••• {card.last4}
                          </p>
                          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                            Expires {card.expMonth}/{card.expYear}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle size={14} className="text-white" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={(e) => handleDeleteCard(card.id, e)}
                          className="p-2 hover:bg-error/10 rounded-lg transition-colors flex-shrink-0"
                        >
                          <Trash2 size={16} className="text-error" />
                        </button>
                      </div>
                    );
                  })}

                  {/* New Card Option */}
                  <div
                    className={`rounded-xl border-2 transition-all ${
                      useNewCard
                        ? 'border-primary bg-primary/5'
                        : 'border-border-light dark:border-border-dark hover:border-primary/50'
                    }`}
                  >
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 p-3 text-left"
                      onClick={() => {
                        setUseNewCard(true);
                        setSelectedCard(null);
                      }}
                    >
                      <div className="w-10 h-10 bg-surface-light dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Plus size={20} className="text-text-secondary-light dark:text-text-secondary-dark" />
                      </div>
                      <span className="font-medium text-text-primary-light dark:text-text-primary-dark flex-1">
                        {savedCards.length > 0 ? 'Use a new card' : 'Enter card details'}
                      </span>
                      {useNewCard && (
                        <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircle size={14} className="text-white" />
                        </div>
                      )}
                    </button>

                    {/* Card Input - Only show when new card is selected */}
                    {useNewCard && (
                      <div className="px-3 pb-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div className="border border-border-light dark:border-border-dark rounded-xl p-4 bg-white dark:bg-gray-800">
                          <CardElement options={cardStyle} />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={saveCard}
                            onChange={(e) => setSaveCard(e.target.checked)}
                            className="w-4 h-4 rounded border-border-light text-primary focus:ring-primary"
                          />
                          <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                            Save card for future purchases
                          </span>
                        </label>
                        <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                          Test: 4242 4242 4242 4242 | 12/34 | 123
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-error/10 border border-error/20 rounded-xl text-error text-sm">
                    {error}
                  </div>
                )}

                <Button
                  variant="primary"
                  onClick={handlePurchase}
                  loading={loading}
                  disabled={!stripe || loading || (!selectedCard && !useNewCard)}
                  fullWidth
                  icon={<CreditCard size={18} />}
                  className="mt-2"
                >
                  Pay {formatPrice(selectedPackage.priceUsdCents)}
                </Button>
              </motion.div>
            )}

            {/* Step 3: Processing */}
            {step === 'processing' && selectedPackage && (
              <motion.div
                key="processing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="py-8 flex flex-col items-center justify-center"
              >
                {/* Animated Processing Icon */}
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 360]
                  }}
                  transition={{
                    scale: { duration: 2, repeat: Infinity, ease: "easeInOut" },
                    rotate: { duration: 3, repeat: Infinity, ease: "linear" }
                  }}
                  className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6"
                >
                  <Coins size={40} className="text-primary" />
                </motion.div>

                {/* Main Status */}
                <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                  Processing Payment
                </h3>
                <p className="text-text-secondary-light dark:text-text-secondary-dark text-center mb-6">
                  Securing your purchase of {(selectedPackage.tokens + selectedPackage.bonusTokens).toLocaleString()} GT
                </p>

                {/* Progress Indicator */}
                <div className="w-full max-w-xs mb-6">
                  <div className="h-2 bg-surface-light dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-primary rounded-full"
                      initial={{ width: "0%" }}
                      animate={{ width: ["0%", "30%", "60%", "90%"] }}
                      transition={{ duration: 8, ease: "easeOut" }}
                    />
                  </div>
                </div>

                {/* Order Details */}
                <div className="w-full bg-surface-light dark:bg-gray-800 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary-light dark:text-text-secondary-dark">Package</span>
                    <span className="font-medium text-text-primary-light dark:text-text-primary-dark">{selectedPackage.name}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-2">
                    <span className="text-text-secondary-light dark:text-text-secondary-dark">Amount</span>
                    <span className="font-bold text-primary">{formatPrice(selectedPackage.priceUsdCents)}</span>
                  </div>
                </div>

                {/* Warning Message */}
                <div className="flex items-start gap-3 p-4 bg-warning/10 border border-warning/20 rounded-xl">
                  <AlertCircle size={20} className="text-warning flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-warning">Please don't close this window</p>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1">
                      Your payment is being processed securely. This may take a few moments.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

// Success Modal
const SuccessModal: React.FC<{
  amount: number;
  onClose: () => void;
}> = ({ amount, onClose }) => {
  const modalContent = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        className="bg-white dark:bg-surface-dark rounded-2xl shadow-xl max-w-sm w-full p-8 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="w-20 h-20 mx-auto mb-6 bg-success/10 rounded-full flex items-center justify-center"
        >
          <CheckCircle size={48} className="text-success" />
        </motion.div>

        <h3 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
          Purchase Complete!
        </h3>
        <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6">
          Your tokens have been added to your wallet
        </p>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <GTChange amount={amount} size="lg" />
        </motion.div>

        <Button variant="primary" onClick={onClose} fullWidth>
          Continue
        </Button>
      </motion.div>
    </motion.div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

// Transaction Item Component
const TransactionItem: React.FC<{ transaction: GTTransaction }> = ({ transaction }) => {
  const isPositive = transaction.amount > 0;

  const getIcon = () => {
    switch (transaction.type) {
      case 'purchase':
        return <CreditCard size={20} className="text-primary" />;
      case 'earn_slip':
      case 'earn_trivia':
      case 'earn_streak':
      case 'earn_engagement':
        return <TrendingUp size={20} className="text-success" />;
      case 'earn_tip':
        return <Gift size={20} className="text-success" />;
      case 'spend_tip':
        return <Gift size={20} className="text-error" />;
      case 'spend_store':
        return <TrendingDown size={20} className="text-error" />;
      case 'refund':
        return <RefreshCw size={20} className="text-info" />;
      case 'admin_adjustment':
        return isPositive ? <ArrowUpRight size={20} className="text-success" /> : <ArrowDownRight size={20} className="text-error" />;
      default:
        return <Coins size={20} className="text-text-secondary-light" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex items-center gap-4 p-4 hover:bg-surface-light dark:hover:bg-gray-800 rounded-xl transition-colors"
    >
      <div className="w-10 h-10 rounded-full bg-surface-light dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
        {getIcon()}
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-medium text-text-primary-light dark:text-text-primary-dark truncate">
          {transaction.description}
        </p>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          {format(transaction.createdAt, 'MMM d, yyyy • h:mm a')}
        </p>
      </div>

      <div className={`text-right flex-shrink-0 ${isPositive ? 'text-success' : 'text-error'}`}>
        <p className="font-semibold">
          {isPositive ? '+' : ''}{transaction.amount.toLocaleString()} GT
        </p>
        <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
          Balance: {transaction.balanceAfter.toLocaleString()}
        </p>
      </div>
    </motion.div>
  );
};

// Main Wallet Page
const WalletPageContent: React.FC = () => {
  const { user, wallet, isNewSignup, redeemedOffer, clearNewSignupFlag } = useAuth();
  const { isTestMode } = useTestMode();
  const { data: transactions, isLoading: transactionsLoading } = useGTTransactions(user?.uid);
  const { packages } = useTokenPackages();

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [purchasedAmount, setPurchasedAmount] = useState(0);
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [showIntro, setShowIntro] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Show welcome modal for new signups with offers
  useEffect(() => {
    if (isNewSignup && redeemedOffer) {
      setShowWelcomeModal(true);
    }
  }, [isNewSignup, redeemedOffer]);

  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false);
    clearNewSignupFlag();
  };

  // Check if intro has been dismissed before
  useEffect(() => {
    const dismissed = localStorage.getItem('gt_intro_dismissed');
    if (dismissed) {
      setShowIntro(false);
    }
  }, []);

  const handleDismissIntro = () => {
    setShowIntro(false);
    localStorage.setItem('gt_intro_dismissed', 'true');
  };

  // Load saved cards (filtered by current Stripe mode)
  const loadSavedCards = React.useCallback(async () => {
    if (!user?.uid) return;
    const currentMode = isTestMode ? 'test' : 'live';
    const basePath = isTestMode ? 'test-mode/v1' : 'v1';
    try {
      const snapshot = await dbGet(`${basePath}/wallet/savedCards/${user.uid}`);
      const data = snapshot.val();
      if (data) {
        const allCards = Object.values(data) as SavedCard[];
        // Filter cards by current Stripe mode, and handle legacy cards without stripeMode
        const filteredCards = allCards.filter(card => {
          // Legacy cards without stripeMode are assumed to be test mode
          const cardMode = card.stripeMode || 'test';
          return cardMode === currentMode;
        });
        setSavedCards(filteredCards.sort((a, b) => b.createdAt - a.createdAt));
      } else {
        setSavedCards([]);
      }
    } catch (err) {
      console.error('Failed to load saved cards:', err);
    }
  }, [user?.uid, isTestMode]);

  useEffect(() => {
    loadSavedCards();
  }, [loadSavedCards]);

  const sortedTransactions = React.useMemo(() => {
    if (!transactions) return [];
    return [...transactions].sort((a, b) => b.createdAt - a.createdAt);
  }, [transactions]);

  const handlePurchaseSuccess = (amount: number) => {
    setPurchasedAmount(amount);
    setShowPurchaseModal(false);
    setShowSuccessModal(true);
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary-light dark:text-text-primary-dark">
            Wallet
          </h1>
          <p className="text-text-secondary-light dark:text-text-secondary-dark mt-1">
            Manage your GroundTokens
          </p>
        </div>
      </div>

      {/* GT Intro Box */}
      <AnimatePresence>
        {showIntro && <GTIntroBox onDismiss={handleDismissIntro} />}
      </AnimatePresence>

      {/* Wallet Balance Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-6 sm:p-8 bg-gradient-to-br from-primary via-primary-600 to-primary-800 text-white relative overflow-hidden"
      >
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Wallet size={24} />
              </div>
              <div>
                <p className="text-white/80 text-sm">Current Balance</p>
                <h2 className="text-3xl sm:text-4xl font-bold">
                  {wallet?.balance.toLocaleString() ?? 0} GT
                </h2>
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={() => setShowPurchaseModal(true)}
              icon={<Plus size={18} />}
              className="bg-white/20 hover:bg-white/30 border-white/30 text-white sm:self-start"
            >
              Buy Tokens
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-white/60 text-xs sm:text-sm">Total Earned</p>
              <p className="font-semibold text-sm sm:text-base">{wallet?.totalEarned.toLocaleString() ?? 0} GT</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-white/60 text-xs sm:text-sm">Total Spent</p>
              <p className="font-semibold text-sm sm:text-base">{wallet?.totalSpent.toLocaleString() ?? 0} GT</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-white/60 text-xs sm:text-sm">Tips Given</p>
              <p className="font-semibold text-sm sm:text-base">{wallet?.totalTippedGiven.toLocaleString() ?? 0} GT</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-white/60 text-xs sm:text-sm">Tips Received</p>
              <p className="font-semibold text-sm sm:text-base">{wallet?.totalTippedReceived.toLocaleString() ?? 0} GT</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Transaction History */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <History size={20} className="text-primary" />
          <h2 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">
            Transaction History
          </h2>
        </div>

        <div className="card divide-y divide-border-light dark:divide-border-dark">
          {transactionsLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : sortedTransactions.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-surface-light dark:bg-gray-800 rounded-full flex items-center justify-center">
                <History size={32} className="text-text-secondary-light dark:text-text-secondary-dark" />
              </div>
              <p className="text-text-secondary-light dark:text-text-secondary-dark">
                No transactions yet
              </p>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
                Buy some GroundTokens to get started!
              </p>
              <Button
                variant="primary"
                onClick={() => setShowPurchaseModal(true)}
                icon={<Plus size={18} />}
                className="mt-4"
              >
                Buy Tokens
              </Button>
            </div>
          ) : (
            sortedTransactions.map((tx) => (
              <TransactionItem key={tx.transactionId} transaction={tx} />
            ))
          )}
        </div>
      </div>

      {/* Purchase Modal */}
      <AnimatePresence>
        {showPurchaseModal && user && (
          <PurchaseFlowModal
            isOpen={showPurchaseModal}
            onClose={() => setShowPurchaseModal(false)}
            onSuccess={handlePurchaseSuccess}
            userId={user.uid}
            savedCards={savedCards}
            onCardSaved={loadSavedCards}
            packages={packages}
            isTestMode={isTestMode}
          />
        )}
      </AnimatePresence>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccessModal && (
          <SuccessModal
            amount={purchasedAmount}
            onClose={() => setShowSuccessModal(false)}
          />
        )}
      </AnimatePresence>

      {/* Signup Offer Welcome Modal */}
      {redeemedOffer && (
        <SignupOfferWelcomeModal
          isOpen={showWelcomeModal}
          onClose={handleCloseWelcomeModal}
          offer={redeemedOffer}
          username={user?.username}
        />
      )}
    </div>
  );
};

// Wrap with Elements provider - uses test mode from context
export const WalletPage: React.FC = () => {
  const { isTestMode } = useTestMode();

  // Memoize stripe instance based on test mode
  const stripePromise = useMemo(() => getStripe(isTestMode), [isTestMode]);

  return (
    <Elements stripe={stripePromise}>
      <WalletPageContent />
    </Elements>
  );
};

export default WalletPage;
